// src/Components/Announcements.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  MenuItem,
  Divider,
  Paper,
  Menu,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Switch,
  Collapse,
  InputAdornment,
  ImageList,
  ImageListItem
} from '@mui/material';
import {
  Add as AddIcon,
  PushPin as PinIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  PriorityHigh as HighPriorityIcon,
  Info as InfoIcon,
  MoreVert as MoreVertIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Settings as SettingsIcon,
  PhotoCamera as PhotoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { getFirestore, collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { formatDistanceToNow } from 'date-fns';
import { sanitizeImageUrl } from '../utils/urlSanitizer';
import PrivacyControl from './PrivacyControl';
import TagMembers from './TagMembers';
import EmojiPicker from './EmojiPicker';
import { notifyTaggedMembers, notifyNewAnnouncement } from '../services/notificationService';
import logger from '../utils/logger';

const Announcements = ({ familyBoardId }) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'medium',
    photos: [],
    privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
    taggedMembers: []
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);

  // Menu and action states
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuAnnouncement, setMenuAnnouncement] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const db = getFirestore();
  const storage = getStorage();

  // Check if current user is admin
  const isAdmin = profile?.isAdmin || profile?.isSuperAdmin || currentUser?.isAdmin;

  // Fetch announcements from NESTED structure
  useEffect(() => {
    if (!familyBoardId) return;

    const q = query(
      collection(db, 'families', familyBoardId, 'announcements'),
      orderBy('pinned', 'desc'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const announcementsData = [];
        querySnapshot.forEach((doc) => {
          announcementsData.push({ id: doc.id, ...doc.data() });
        });
        setAnnouncements(announcementsData);
        setLoading(false);
        setError(null);
      },
      (error) => {
        logger.error('Error fetching announcements:', error);
        setError('Failed to load announcements');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, familyBoardId]);

  const handleOpenDialog = (announcement = null) => {
    if (announcement) {
      setEditingId(announcement.id);
      setFormData({
        title: announcement.title,
        content: announcement.content,
        priority: announcement.priority || 'medium',
        photos: announcement.photos || [],
        privacy: announcement.privacy || { visibility: 'public', memberIds: [], groupTypes: [] },
        taggedMembers: announcement.taggedMembers || [],
        _previousTaggedMembers: announcement.taggedMembers || []
      });
      // Show advanced if there are privacy settings or tags
      if (announcement.privacy?.visibility === 'private' || announcement.taggedMembers?.length > 0) {
        setShowAdvanced(true);
      }
    } else {
      setEditingId(null);
      setFormData({
        title: '',
        content: '',
        priority: 'medium',
        photos: [],
        privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
        taggedMembers: []
      });
      setShowAdvanced(false);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormData({
      title: '',
      content: '',
      priority: 'medium',
      photos: [],
      privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
      taggedMembers: []
    });
    setShowAdvanced(false);
  };

  // Photo upload handler
  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length + formData.photos.length > 5) {
      setError('Maximum 5 photos allowed');
      return;
    }

    setPhotoUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const storageRef = ref(storage, `families/${familyBoardId}/announcements/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
      });

      const photoUrls = await Promise.all(uploadPromises);
      setFormData({ ...formData, photos: [...formData.photos, ...photoUrls] });
    } catch (err) {
      logger.error('Error uploading photos:', err);
      setError('Failed to upload photos');
    } finally {
      setPhotoUploading(false);
    }
  };

  const removePhoto = (index) => {
    setFormData({
      ...formData,
      photos: formData.photos.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Title and content are required');
      return;
    }

    // Validate privacy settings
    if (formData.privacy.visibility === 'private' &&
        formData.privacy.memberIds.length === 0 &&
        formData.privacy.groupTypes.length === 0) {
      setError('Please select at least one member or group for private announcements');
      return;
    }

    try {
      const announcementData = {
        title: formData.title,
        content: formData.content,
        priority: formData.priority,
        photos: formData.photos || [],
        authorId: currentUser.uid,
        authorName: currentUser.displayName || profile?.displayName || 'Anonymous',
        privacy: formData.privacy,
        taggedMembers: formData.taggedMembers || [],
        pinned: editingId ? undefined : false, // Only set pinned for new announcements
        ...(editingId ? { updatedAt: serverTimestamp() } : { createdAt: serverTimestamp() })
      };

      // Remove undefined fields
      Object.keys(announcementData).forEach(key => {
        if (announcementData[key] === undefined) {
          delete announcementData[key];
        }
      });

      const createdByName = currentUser.displayName || profile?.displayName || 'Anonymous';
      if (editingId) {
        await updateDoc(
          doc(db, 'families', familyBoardId, 'announcements', editingId),
          announcementData
        );

        // Notify members newly tagged on edit
        const previousTagged = editingId ? (formData._previousTaggedMembers || []) : [];
        const newTagged = (formData.taggedMembers || []).filter(id => !previousTagged.includes(id));
        if (newTagged.length > 0) {
          await notifyTaggedMembers(familyBoardId, newTagged, {
            type: 'announcement',
            id: editingId,
            title: formData.title,
            createdBy: currentUser.uid,
            createdByName,
            actionUrl: '/announcements'
          });
        }

        setSuccess('Announcement updated successfully');
      } else {
        const docRef = await addDoc(
          collection(db, 'families', familyBoardId, 'announcements'),
          announcementData
        );

        // Notify all family members about the new announcement
        await notifyNewAnnouncement(
          familyBoardId,
          { id: docRef.id, title: formData.title },
          currentUser.uid,
          createdByName
        );

        setSuccess('Announcement created successfully');
      }

      handleCloseDialog();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error saving announcement:', error);
      setError('Failed to save announcement');
    }
  };

  const handleTogglePin = async (id, currentPinned) => {
    try {
      await updateDoc(
        doc(db, 'families', familyBoardId, 'announcements', id),
        { pinned: !currentPinned, updatedAt: serverTimestamp() }
      );
    } catch (error) {
      logger.error('Error toggling pin:', error);
      setError('Failed to update announcement');
    }
  };

  // Handle menu open
  const handleMenuOpen = (event, announcement) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuAnnouncement(announcement);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuAnnouncement(null);
  };

  // Check if user can manage this announcement (edit/delete/archive)
  const canManageAnnouncement = (announcement) => {
    return announcement.authorId === currentUser?.uid || isAdmin;
  };

  // Handle archive/unarchive
  const handleArchive = async (announcement) => {
    try {
      const newArchivedState = !announcement.isArchived;
      await updateDoc(doc(db, 'families', familyBoardId, 'announcements', announcement.id), {
        isArchived: newArchivedState,
        archivedAt: newArchivedState ? serverTimestamp() : null,
        archivedBy: newArchivedState ? currentUser.uid : null
      });
      setSuccess(newArchivedState ? 'Announcement archived' : 'Announcement restored');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error archiving announcement:', error);
      setError('Failed to archive announcement');
    }
    handleMenuClose();
  };

  // Handle delete confirmation dialog
  const handleDeleteClick = (announcement) => {
    setAnnouncementToDelete(announcement);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // Handle actual delete
  const handleDelete = async () => {
    if (!announcementToDelete) return;

    try {
      await deleteDoc(doc(db, 'families', familyBoardId, 'announcements', announcementToDelete.id));
      setSuccess('Announcement deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error deleting announcement:', error);
      setError('Failed to delete announcement');
    }
    setDeleteDialogOpen(false);
    setAnnouncementToDelete(null);
  };

  // Helper function to check if user can view an announcement based on privacy settings
  const canViewAnnouncement = (announcement) => {
    // Announcement author can always see their own announcements
    if (announcement.authorId === currentUser?.uid) {
      return true;
    }

    // Admins can see all announcements
    if (isAdmin) {
      return true;
    }

    // If no privacy settings or public, everyone can see
    if (!announcement.privacy || announcement.privacy.visibility === 'public') {
      return true;
    }

    // For private announcements, check if user is in the allowed list
    if (announcement.privacy.visibility === 'private') {
      // Check if user ID is in memberIds
      if (announcement.privacy.memberIds && announcement.privacy.memberIds.includes(currentUser?.uid)) {
        return true;
      }

      // Check if user belongs to any of the allowed groups
      if (announcement.privacy.groupTypes && announcement.privacy.groupTypes.length > 0 && profile?.groups) {
        const userGroups = profile.groups || [];
        const hasGroupAccess = announcement.privacy.groupTypes.some(groupType =>
          userGroups.includes(groupType)
        );
        if (hasGroupAccess) {
          return true;
        }
      }

      // User doesn't have access to this private announcement
      return false;
    }

    return true;
  };

  // Filter announcements based on privacy settings and archive status
  const visibleAnnouncements = announcements.filter(announcement => {
    if (!canViewAnnouncement(announcement)) return false;
    if (announcement.isArchived && !showArchived) return false;
    return true;
  });

  // Count archived announcements for the toggle label
  const archivedCount = announcements.filter(a => a.isArchived && canViewAnnouncement(a)).length;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <HighPriorityIcon fontSize="small" />;
      case 'medium':
        return <InfoIcon fontSize="small" />;
      case 'low':
        return <InfoIcon fontSize="small" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Family Announcements
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Share important updates and milestones with your family
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {archivedCount > 0 && (
            <FormControlLabel
              control={
                <Switch
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  size="small"
                />
              }
              label={`Show archived (${archivedCount})`}
              sx={{ mr: 0 }}
            />
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New Announcement
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Announcements List */}
      {visibleAnnouncements.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.default' }}>
          <Box sx={{ mb: 2 }}>
            <InfoIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No announcements yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Be the first to share something with your family!
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create First Announcement
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {visibleAnnouncements.map((announcement) => (
            <Grid item xs={12} key={announcement.id}>
              <Card
                elevation={announcement.pinned ? 4 : 2}
                sx={{
                  position: 'relative',
                  border: announcement.pinned ? '2px solid' : 'none',
                  borderColor: 'primary.main',
                  transition: 'all 0.3s',
                  opacity: announcement.isArchived ? 0.7 : 1,
                  borderLeft: announcement.isArchived ? '4px solid' : announcement.pinned ? '2px solid' : 'none',
                  borderLeftColor: announcement.isArchived ? 'warning.main' : 'primary.main',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                }}
              >
                {announcement.pinned && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bgcolor: 'primary.main',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderBottomLeftRadius: 8
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PinIcon fontSize="small" />
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Pinned
                      </Typography>
                    </Box>
                  </Box>
                )}

                <CardContent sx={{ pb: 1 }}>
                  {/* Priority, Privacy & Time */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={announcement.priority || 'medium'}
                      color={getPriorityColor(announcement.priority)}
                      icon={getPriorityIcon(announcement.priority)}
                      sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                    />
                    {announcement.privacy?.visibility === 'private' && (
                      <Chip
                        size="small"
                        label="Private"
                        variant="outlined"
                        color="secondary"
                        sx={{ fontWeight: 500 }}
                      />
                    )}
                    {announcement.isArchived && (
                      <Chip
                        label="Archived"
                        size="small"
                        color="warning"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                      <TimeIcon fontSize="small" />
                      <Typography variant="caption">
                        {announcement.createdAt && formatDistanceToNow(announcement.createdAt.toDate(), { addSuffix: true })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Title & Content */}
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                    {announcement.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {announcement.content}
                  </Typography>

                  {/* Announcement Photos */}
                  {announcement.photos && announcement.photos.length > 0 && (
                    <ImageList cols={Math.min(announcement.photos.length, 3)} gap={8} sx={{ mb: 2 }}>
                      {announcement.photos.map((url, idx) => (
                        <ImageListItem key={idx}>
                          <img
                            src={sanitizeImageUrl(url)}
                            alt={`Announcement ${idx + 1}`}
                            style={{ borderRadius: 8, maxHeight: 200, objectFit: 'cover' }}
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  )}

                  {/* Tagged Members */}
                  {announcement.taggedMembers && announcement.taggedMembers.length > 0 && (
                    <Box sx={{ mb: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                        Tagged:
                      </Typography>
                      {announcement.taggedMembers.map((memberId, index) => (
                        <Chip
                          key={index}
                          size="small"
                          icon={<PersonIcon />}
                          label={memberId}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Author Info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {announcement.authorName || 'Anonymous'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Posted {announcement.createdAt && announcement.createdAt.toDate().toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleTogglePin(announcement.id, announcement.pinned)}
                    color={announcement.pinned ? 'primary' : 'default'}
                    title={announcement.pinned ? 'Unpin' : 'Pin'}
                  >
                    <PinIcon />
                  </IconButton>
                  {canManageAnnouncement(announcement) && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, announcement)}
                      sx={{ color: 'text.secondary' }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => {
          handleOpenDialog(menuAnnouncement);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuAnnouncement && handleArchive(menuAnnouncement)}>
          <ListItemIcon>
            {menuAnnouncement?.isArchived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{menuAnnouncement?.isArchived ? 'Restore' : 'Archive'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuAnnouncement && handleDeleteClick(menuAnnouncement)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Announcement' : 'Create New Announcement'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              multiline
              rows={4}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <EmojiPicker
                      onEmojiSelect={(emoji) => setFormData({ ...formData, content: formData.content + emoji })}
                      size="small"
                    />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              fullWidth
              select
              label="Priority"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            >
              <MenuItem value="low">Low Priority</MenuItem>
              <MenuItem value="medium">Medium Priority</MenuItem>
              <MenuItem value="high">High Priority</MenuItem>
            </TextField>

            {/* Photo Upload */}
            <Box>
              <input
                type="file"
                ref={photoInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
              />
              <Button
                startIcon={photoUploading ? <CircularProgress size={16} /> : <PhotoIcon />}
                onClick={() => photoInputRef.current?.click()}
                disabled={photoUploading || formData.photos.length >= 5}
                variant="outlined"
                size="small"
              >
                {photoUploading ? 'Uploading...' : `Add Photos (${formData.photos.length}/5)`}
              </Button>
              {formData.photos.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {formData.photos.map((url, index) => (
                    <Box key={index} sx={{ position: 'relative' }}>
                      <img
                        src={sanitizeImageUrl(url)}
                        alt={`Attachment ${index + 1}`}
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removePhoto(index)}
                        sx={{
                          position: 'absolute', top: -8, right: -8,
                          bgcolor: 'background.paper', boxShadow: 1,
                          '&:hover': { bgcolor: 'error.light', color: 'white' }
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Advanced Options Toggle */}
            <Button
              variant="text"
              startIcon={<SettingsIcon />}
              endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowAdvanced(!showAdvanced)}
              sx={{ justifyContent: 'flex-start', color: 'text.secondary' }}
            >
              Advanced Options
            </Button>

            <Collapse in={showAdvanced}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Divider>
                  <Chip label="Privacy & Tagging" size="small" />
                </Divider>

                {/* Privacy Control */}
                <PrivacyControl
                  familyCode={familyBoardId}
                  value={formData.privacy}
                  onChange={(privacy) => setFormData({ ...formData, privacy })}
                  label="Who can see this announcement?"
                />

                {/* Tag Members */}
                <TagMembers
                  familyCode={familyBoardId}
                  value={formData.taggedMembers}
                  onChange={(taggedMembers) => setFormData({ ...formData, taggedMembers })}
                  label="Tag Family Members"
                  helperText="Tagged members will receive a notification about this announcement"
                />
              </Box>
            </Collapse>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Announcement?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete this announcement?
          </Typography>
          {announcementToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                "{announcementToDelete.title}"
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. Consider archiving instead if you want to preserve the announcement.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Announcements;
